import { useCallback, useMemo } from 'react';
import {
  CodeBlock,
  DataTable,
  DataTableHeader,
  DataTableHeaderActions,
  DataTableHeaderActionsLeftSection,
  DataTableHeaderActionsRightSection,
  DataTableHeaderColumnsMenu,
  DataTableHeaderRow,
  DataTableHeaderSortMenu,
  DataTableTable,
  Panel,
  PanelInset,
  Text,
  Tooltip,
  Truncated,
  type DataTableFeatures,
} from '@customerio/pluma-components/react';
import { createColumnHelper, type ColumnDef } from '@tanstack/table-core';
import type { Row } from '@tanstack/react-table';
import type { Collection } from '@/services/db/collection/entity';
import type { CollectionItem } from '@/services/db/collection/queries';

export type CollectionItemsTableSorting = {
  id: string;
  desc: boolean;
};

type CollectionItemsTableProps = {
  collection: Collection;
  isLoading?: boolean;
  items: CollectionItem[];
  sorting?: CollectionItemsTableSorting;
  visibleColumns: string[];
  canEditDisplayedColumns: boolean;
  maxDisplayedColumns: number;
  onSortingChange: (sorting: CollectionItemsTableSorting) => void;
  onVisibleColumnsChange: (attributes: string[]) => void;
};

const columnHelper = createColumnHelper<DataTableFeatures, CollectionItem>();

function uniqueAttributes(attributes: string[]) {
  return [...new Set(attributes)];
}

function stringifyValue(value: unknown): string {
  if (value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value) ?? '';
  return String(value);
}

type ValueCellProps = {
  value: unknown;
};

function ValueCell({ value }: ValueCellProps) {
  const displayValue = stringifyValue(value);

  return (
    <Tooltip
      as="span"
      placement="top"
      isDisabled={typeof value !== 'object'}
      content={displayValue}
      unsafe_tooltipClassName="fs-mask"
    >
      <Truncated className="fs-mask">{displayValue}</Truncated>
    </Tooltip>
  );
}

type ExpandedRowProps<TData extends CollectionItem = CollectionItem> = {
  row: Row<DataTableFeatures, TData>;
};

function ExpandedRow<TData extends CollectionItem = CollectionItem>({ row }: ExpandedRowProps<TData>) {
  return <CodeBlock language="json" code={JSON.stringify(row.original, null, 2)} className="fs-mask" />;
}

function createColumnIdMap(schema: string[]): Map<string, string> {
  return new Map(
    schema.map((attribute, index) => [attribute, attribute.trim() !== '' ? attribute : `__empty_key_${index}`]),
  );
}

function createColumns(
  collection: Collection,
  columnIdMap: Map<string, string>,
): ColumnDef<DataTableFeatures, CollectionItem, unknown>[] {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return collection.schema.map((attribute) =>
    columnHelper.accessor((row) => row[attribute], {
      id: columnIdMap.get(attribute),
      header: attribute,
      enableSorting: attribute.trim() !== '',
      meta: { width: '1fr', minWidth: 160 },
      cell: (info) => <ValueCell value={info.getValue()} />,
    }),
  ) as ColumnDef<DataTableFeatures, CollectionItem, unknown>[];
}

type UseCollectionColumnModelProps = Pick<
  CollectionItemsTableProps,
  'collection' | 'visibleColumns' | 'onVisibleColumnsChange'
>;

function useCollectionColumnModel({
  collection,
  visibleColumns,
  onVisibleColumnsChange,
}: UseCollectionColumnModelProps) {
  const columnIdMap = useMemo(() => createColumnIdMap(collection.schema), [collection.schema]);
  const schema = useMemo(() => uniqueAttributes(collection.schema), [collection.schema]);
  const visibleSchema = useMemo(
    () => uniqueAttributes(visibleColumns).filter((attribute) => schema.includes(attribute)),
    [schema, visibleColumns],
  );
  const tableCollection = useMemo(() => ({ ...collection, schema }), [collection, schema]);
  const columns = useMemo(() => createColumns(tableCollection, columnIdMap), [columnIdMap, tableCollection]);
  const attributeByColumnId = useMemo(
    () => new Map([...columnIdMap].map(([attribute, id]) => [id, attribute])),
    [columnIdMap],
  );
  // The columns menu reorders whatever it is given, so hidden attributes have to
  // be in the order too or they would have no position to be revealed into.
  const columnOrder = useMemo(() => {
    const hidden = schema.filter((attribute) => !visibleSchema.includes(attribute));
    return [...visibleSchema, ...hidden].map((attribute) => columnIdMap.get(attribute) ?? attribute);
  }, [columnIdMap, schema, visibleSchema]);
  const columnVisibility = useMemo(
    () =>
      schema.reduce<Record<string, boolean>>((visibility, attribute) => {
        const id = columnIdMap.get(attribute) ?? attribute;
        visibility[id] = visibleSchema.includes(attribute);
        return visibility;
      }, {}),
    [columnIdMap, schema, visibleSchema],
  );
  const emitVisibleColumns = useCallback(
    (ids: string[]) => {
      onVisibleColumnsChange(ids.map((id) => attributeByColumnId.get(id) ?? id));
    },
    [attributeByColumnId, onVisibleColumnsChange],
  );
  const handleColumnVisibilityChange = useCallback(
    (nextVisibility: Record<string, boolean>) => {
      emitVisibleColumns(columnOrder.filter((id) => nextVisibility[id] === true));
    },
    [columnOrder, emitVisibleColumns],
  );
  const handleColumnOrderChange = useCallback(
    (nextOrder: string[]) => {
      emitVisibleColumns(nextOrder.filter((id) => columnVisibility[id] === true));
    },
    [columnVisibility, emitVisibleColumns],
  );

  return { columns, columnOrder, columnVisibility, handleColumnOrderChange, handleColumnVisibilityChange };
}

export function CollectionItemsTable({
  collection,
  isLoading,
  items,
  sorting,
  visibleColumns,
  canEditDisplayedColumns,
  maxDisplayedColumns,
  onSortingChange,
  onVisibleColumnsChange,
}: CollectionItemsTableProps) {
  const { columns, columnOrder, columnVisibility, handleColumnOrderChange, handleColumnVisibilityChange } =
    useCollectionColumnModel({ collection, visibleColumns, onVisibleColumnsChange });
  const sortingConfig = useMemo(
    () => ({
      value: sorting,
      onChange: onSortingChange,
      isManual: true,
    }),
    [onSortingChange, sorting],
  );

  if (columns.length === 0) {
    return <Text color="subtle">This collection has no attributes to display.</Text>;
  }

  return (
    <DataTable
      isLoading={isLoading}
      caption="A list of entries in the collection."
      data={items}
      columns={columns}
      getRowId={(_row, index) => String(index)}
      withSorting
      sorting={sortingConfig}
      withRowExpanding
      // v9 defaults getCanExpand() to !!row.subRows?.length; these rows are flat.
      getRowCanExpand={() => true}
      expandedRowComponent={ExpandedRow}
      columnOrder={columnOrder}
      columnVisibility={columnVisibility}
      withColumnsSettings={canEditDisplayedColumns}
      columnsSettings={{ withSearch: true, maxVisibleColumns: maxDisplayedColumns }}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnVisibilityChange={handleColumnVisibilityChange}
    >
      <Panel>
        <DataTableHeader>
          <DataTableHeaderRow>
            <DataTableHeaderActions>
              <DataTableHeaderActionsLeftSection />
              <DataTableHeaderActionsRightSection>
                {canEditDisplayedColumns ? <DataTableHeaderColumnsMenu /> : null}
                <DataTableHeaderSortMenu />
              </DataTableHeaderActionsRightSection>
            </DataTableHeaderActions>
          </DataTableHeaderRow>
        </DataTableHeader>
        <PanelInset>
          <DataTableTable />
        </PanelInset>
      </Panel>
    </DataTable>
  );
}
